import { UserIcon, AcademicCapIcon, CalendarDaysIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { useRef, useState, useEffect } from 'react';

interface StepNavigationProps {
  currentStep: number;
  triggerFormAction: (targetStep: number) => void;
  completedSteps: string[];
}

export const steps = [
  {
    id: 1,
    value: 'BASIC_INFO',
    label: 'The Basics',
    icon: UserIcon,
    description: "Welcome to Sendit Gigs! Let's create your profile in 3 easy steps.",
  },
  {
    id: 2,
    value: 'RATES',
    label: 'Shift Details',
    icon: CalendarDaysIcon,
    description: 'Set the shift types and rates that best align with your goals.',
  },
  {
    id: 3,
    value: 'PROFESSIONAL_TRAITS',
    label: 'Professional Details',
    icon: AcademicCapIcon,
    description: 'Share your professional background and expertise.',
  },
];

// Export the type for use in other components
export type Step = (typeof steps)[number];

export const StepNavigation = ({
  currentStep,
  triggerFormAction,
  completedSteps,
}: StepNavigationProps) => {
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [outlineStyles, setOutlineStyles] = useState({
    width: 0,
    height: 0,
    top: 0,
    left: 0,
  });

  const updateOutlineStyles = (delay = 50) => {
    setTimeout(() => {
      const activeButton = buttonRefs.current[currentStep - 1];
      if (activeButton) {
        const rect = activeButton.getBoundingClientRect();
        const parentRect = activeButton.parentElement?.getBoundingClientRect();

        if (parentRect) {
          // Inset -4 centers the +8 width/height expansion on the button. Use top/left on the
          // absolute outline — not transform — so position does not depend on flex "static
          // placement" (which differs between breakpoints and broke lg vs p-[15px] vs p-4).
          const inset = 4;
          setOutlineStyles({
            width: rect.width + inset * 2,
            height: rect.height + inset * 2,
            top: rect.top - parentRect.top - inset,
            left: rect.left - parentRect.left - inset,
          });
        }
      }
    }, delay);
  };

  useEffect(() => {
    updateOutlineStyles(); // Default 50ms delay for step changes

    // Debounce the resize handler to prevent too many updates
    let resizeTimer: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => updateOutlineStyles(250), 100); // 250ms delay for resize
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimer);
    };
  }, [currentStep]);

  return (
    <div className="tw:flex tw:justify-between tw:px-[15px] tw:relative tw:lg:p-4 tw:lg:flex-col tw:lg:justify-start tw:lg:gap-6 tw:lg:w-fit tw:lg:h-fit tw:rounded-lg tw:lg:rounded-none tw:lg:border-0 tw:mt-0 tw:lg:m-0 tw:dark:bg-zinc-600 tw:dark:border-zinc-500 tw:dark:lg:bg-transparent">
      {/* Connecting lines container - hide on large screens and modify for vertical layout */}
      <div className="tw:absolute tw:top-1/2 tw:left-0 tw:right-0 tw:flex tw:px-4 tw:lg:hidden">
        {steps.slice(0, -1).map((_, index) => (
          <div
            key={index}
            className="tw:w-full tw:h-[2px] tw:bg-gray-300 tw:dark:bg-zinc-500"
            style={{
              transform: 'translateY(-50%)',
            }}
          />
        ))}
      </div>

      {/* TODO: figure out this spacing once vertical layout is settled */}
      {/* <div className="hidden lg:block lg:absolute lg:left-[8.5rem] lg:top-[2.5rem] lg:h-[calc(100%-5rem)] lg:w-[2px] lg:bg-gray-300" /> */}

      {/* Outline div */}
      <div
        className="tw:pointer-events-none tw:absolute tw:border-2 tw:border-brand-green-1 tw:rounded-full tw:transition-all tw:duration-300 tw:ease-in-out"
        style={{
          width: outlineStyles.width,
          height: outlineStyles.height,
          top: outlineStyles.top,
          left: outlineStyles.left,
        }}
      />

      {/* Buttons */}
      {steps.map((step, index) => (
        <button
          key={step.id}
          type="button"
          data-testid={`onboarding-step-nav-${step.id}`}
          ref={(el) => (buttonRefs.current[index] = el)}
          className={`step-nav-item tw:hover:cursor-pointer tw:relative tw:z-20
            ${
              currentStep === step.id
                ? 'tw:bg-brand-green-1 tw:text-white tw:ring-2 tw:ring-white tw:dark:text-zinc-300 tw:dark:ring-zinc-600'
                : 'tw:bg-gray-300 tw:text-gray-600 tw:dark:bg-zinc-500 tw:dark:text-zinc-300'
            }
            tw:p-2 tw:sm:w-16 tw:sm:h-16 tw:sm:flex tw:sm:items-center tw:sm:justify-center
            tw:md:w-auto tw:md:h-auto tw:md:px-4 tw:md:py-2 tw:md:text-lg
            tw:lg:grid tw:lg:grid-cols-[2.5rem_1fr_2.5rem] tw:lg:items-center tw:lg:w-full tw:lg:px-4
            tw:!rounded-full tw:transition-all tw:duration-300 tw:ease-in-out`}
          onClick={() => {
            triggerFormAction(step.id);
          }}
        >
          {completedSteps.includes(step.value) ? (
            <CheckCircleIcon
              className={`tw:w-6 tw:h-6 tw:block tw:ml:mr-2 tw:transition-all tw:duration-300 tw:ease-in-out ${
                currentStep === step.id
                  ? 'tw:text-white'
                  : 'tw:text-brand-green-1 tw:dark:text-zinc-300'
              }`}
            />
          ) : (
            <step.icon className="tw:w-6 tw:h-6 tw:block tw:ml:mr-2" />
          )}
          <span className="tw:hidden tw:ml:block tw:lg:text-center">{step.label}</span>
          <div className="tw:hidden tw:lg:block" /> {/* Empty spacer */}
        </button>
      ))}
    </div>
  );
};
